import { useEffect, useRef } from 'react';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { UnitsEnum } from '../../../services/entities/impl/blfApi/apiConstants';
import { useGetCustomersQuery } from '../../../services/entities/impl/blfApi/endpoints/customers';
import {
  useAddUserMutation,
  useDeleteUserMutation,
  useGetUserMeQuery,
  useGetUsersQuery,
  useUpdateUserMutation,
} from '../../../services/entities/impl/blfApi/endpoints/users';
import {
  UserRolesEnum,
  UserSettingsSchema,
} from '../../../services/entities/impl/blfApi/schemas/user';
import { useLanguageOptions } from '../../../services/translation/hooks/useLanguageOptions';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import {
  OnboardFormStepEnum,
  selectCurrOnboardUserID,
  selectOnboardFormStep,
  setCurrOnboardUserID,
} from '../onboardingSlice';
import { RolesSubForm } from './RolesSubForm';
import {
  FormController,
  GenericForm,
  GenericFormProps,
} from './generic/GenericForm';
import { ExtraValidationFunction } from './generic/context/CRUDContext';
import { FormFieldType } from './generic/crud/components/FormFieldType';
import { checkFieldUniqueness } from './generic/crud/utils/checkFieldUniqueness';
import { extractIDFromResponse } from './generic/crud/utils/extractIDFromResponse';
import { toSelectOptions } from './generic/crud/utils/toSelectOptions';

const UserSchemaIn = Yup.object().shape({
  customerID: Yup.string().required('Customer ID is required'),
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  settings: UserSettingsSchema.nullable(),
});

const UserSchemaOut = Yup.object().shape({
  id: Yup.string().required('ID is required'),
  customerID: Yup.string().required('Customer ID is required'),
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  settings: UserSettingsSchema.nullable(),
});

const useGetCurrOnboardUser = () => {
  const currOnboardUser = useAppSelector(selectCurrOnboardUserID);
  // Using all users query to correctly handle user mutations.
  // Mostly because of correct handling of user roles mutations.
  const {
    currentData: currUser,
    isLoading,
    isFetching,
  } = useGetUsersQuery(undefined, {
    selectFromResult: ({ data, isLoading, isFetching }) => {
      return {
        currentData: data?.find((user) => user.id === currOnboardUser),
        isLoading,
        isFetching,
      };
    },
  });

  return {
    currentData: currOnboardUser ? currUser || null : null,
    isLoading,
    isFetching,
  };
};

export const UserForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const onboardFormStep = useAppSelector(selectOnboardFormStep);
  const { currentData: customers } = useGetCustomersQuery();
  const { currentData: users } = useGetUsersQuery();
  const { currentData: authorizedUser } = useGetUserMeQuery();
  const { currentData: currEntity } = useGetCurrOnboardUser();
  const { t } = useTranslation();
  const [handleAdd] = useAddUserMutation();
  const [handleUpdate] = useUpdateUserMutation();
  const [handleDelete] = useDeleteUserMutation();

  // It's ok if object in console looks like non-reactive
  // it's react mechanism that protects from infinite updates
  // so object is resolving as soon as be accessed
  const formControlsRef = useRef<FormController | null>(null);

  const languagesOptions = useLanguageOptions();
  const unitsOptions = toSelectOptions(UnitsEnum);

  const extraValidation: ExtraValidationFunction<
    Yup.InferType<typeof UserSchemaIn>
  > = (values) => {
    return checkFieldUniqueness({
      dataset: users,
      fieldsToCheck: ['email'],
      values: values,
      currentItemId: currEntity?.id,
    });
  };

  // If add is selected, reset the current user so the roles form is reset
  useEffect(() => {
    if (onboardFormStep === OnboardFormStepEnum.Add) {
      dispatch(setCurrOnboardUserID(''));
    }
  }, [onboardFormStep, dispatch]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmitSuccess = async (res: any) => {
    const id = extractIDFromResponse(res);
    if (id) {
      dispatch(setCurrOnboardUserID(id));
    }
  };

  const crudConfig: GenericFormProps<
    Yup.InferType<typeof UserSchemaIn>,
    Yup.InferType<typeof UserSchemaOut>
  > = {
    handleAdd,
    handleUpdate,
    handleDelete,
    getCurrEntityHook: useGetCurrOnboardUser,
    extraValidation,
    onSubmitSuccess,
    yupSchemaIn: UserSchemaIn,
    yupSchemaOut: UserSchemaOut,
    slots: {
      [OnboardFormStepEnum.Add]: {
        renderAfter: () => (
          <RolesSubForm useGetCurrOnboardUserHook={useGetCurrOnboardUser} />
        ),
        props: {
          gridConfig: {
            numberOfColumns: 2,
          },
          SubmitButton: {
            config: {
              title: 'Add User',
            },
          },
          fieldsConfig: {
            customerID: {
              type: FormFieldType.Select,
              label: t('Customer', { ns: 'common' }),
              initialValue: '',
              config: {
                items:
                  customers?.map((c) => ({ value: c.id, label: c.name })) || [],
              },
            },
            firstName: {
              type: FormFieldType.TextField,
              label: t('First Name', { ns: 'common' }),
              initialValue: '',
            },
            lastName: {
              type: FormFieldType.TextField,
              label: t('Last Name', { ns: 'common' }),
              initialValue: '',
            },
            email: {
              type: FormFieldType.TextField,
              label: t('Email', { ns: 'common' }),
              initialValue: '',
            },
            settings: {
              type: FormFieldType.Nested,
              label: t('Settings', { ns: 'common' }),
              gridConfig: {
                numberOfColumns: 2,
              },
              config: {
                language: {
                  type: FormFieldType.Select,
                  label: t('Language', { ns: 'common' }),
                  initialValue: 'en',
                  config: {
                    items: languagesOptions,
                  },
                },
                units: {
                  type: FormFieldType.Select,
                  label: t('Units', { ns: 'common' }),
                  initialValue: UnitsEnum.imperial,
                  config: {
                    items: unitsOptions,
                  },
                },
                onboardingComplete: {
                  type: FormFieldType.Checkbox,
                  label: t('Onboarding Complete', { ns: 'common' }),
                  initialValue: false,
                },
              },
            },
          },
        },
      },
      [OnboardFormStepEnum.Edit]: {
        renderAfter: () => (
          <RolesSubForm useGetCurrOnboardUserHook={useGetCurrOnboardUser} />
        ),
        props: {
          gridConfig: {
            numberOfColumns: 2,
          },
          SubmitButton: {
            config: {
              title: 'Edit User',
            },
          },
          textOverrides: {
            entityIsNotSelected: t('Select User first.', { ns: 'common' }),
          },
          hiddenFields: ['id'],
          fieldsConfig: {
            id: {
              // hidden by hiddenFields config prop but needed to set the edit id
              type: FormFieldType.TextField,
              label: 'ID',
            },
            customerID: {
              type: FormFieldType.Select,
              label: t('Customer', { ns: 'common' }),
              config: {
                items:
                  customers?.map((c) => ({ value: c.id, label: c.name })) || [],
              },
            },
            firstName: {
              type: FormFieldType.TextField,
              label: t('First Name', { ns: 'common' }),
            },
            lastName: {
              type: FormFieldType.TextField,
              label: t('Last Name', { ns: 'common' }),
            },
            email: {
              type: FormFieldType.TextField,
              label: t('Email', { ns: 'common' }),
            },
            settings: {
              type: FormFieldType.Nested,
              label: t('Settings', { ns: 'common' }),
              gridConfig: {
                numberOfColumns: 2,
              },
              config: {
                language: {
                  type: FormFieldType.Select,
                  label: t('Language', { ns: 'common' }),
                  config: {
                    items: languagesOptions,
                  },
                },
                units: {
                  type: FormFieldType.Select,
                  label: t('Units', { ns: 'common' }),
                  config: {
                    items: unitsOptions,
                  },
                },
                onboardingComplete: {
                  type: FormFieldType.Checkbox,
                  label: t('Onboarding Complete', { ns: 'common' }),
                },
              },
            },
          },
        },
      },
      [OnboardFormStepEnum.View]: {
        props: {
          textOverrides: {
            entityIsNotSelected: t('Select User first.', { ns: 'common' }),
          },
          fieldsConfig: {
            id: {
              type: FormFieldType.TextDisplay,
              label: t('ID'),
              initialValue: '',
            },
            customerID: {
              type: FormFieldType.TextDisplay,
              label: t('Customer', { ns: 'common' }),
              config: {
                valueGetter: (field) =>
                  customers?.find((c) => c.id === field.value)?.name ||
                  t('Customer Not Found', { ns: 'common' }),
              },
            },
            firstName: {
              type: FormFieldType.TextDisplay,
              label: t('First Name', { ns: 'common' }),
            },
            lastName: {
              type: FormFieldType.TextDisplay,
              label: t('Last Name', { ns: 'common' }),
            },
            email: {
              type: FormFieldType.TextDisplay,
              label: t('Email', { ns: 'common' }),
            },
            settings: {
              type: FormFieldType.Nested,
              label: t('Settings', { ns: 'common' }),
              config: {
                language: {
                  type: FormFieldType.TextDisplay,
                  label: t('Language', { ns: 'common' }),
                  config: {
                    valueGetter: (field) =>
                      languagesOptions.find((l) => l.value === field.value)
                        ?.label || t('Language Not Found', { ns: 'common' }),
                  },
                },
                units: {
                  type: FormFieldType.TextDisplay,
                  label: t('Units', { ns: 'common' }),
                  config: {
                    valueGetter: (field) => t(field.value),
                  },
                },
                onboardingComplete: {
                  type: FormFieldType.CheckboxDisplay,
                  label: t('Onboarding Complete', { ns: 'common' }),
                  config: {
                    valueGetter: (field) => t(field.value),
                  },
                },
              },
            },
          },
        },
      },
      [OnboardFormStepEnum.Delete]: {
        props: {
          submitCondition: () =>
            !authorizedUser?.roles
              .map((role) => role.name)
              .includes(UserRolesEnum.root),
          SubmitButton: {
            config: {
              title: 'Delete User',
            },
          },
          textOverrides: {
            entityIsNotSelected: t('Select User first.', { ns: 'common' }),
          },
          fieldsConfig: {
            id: {
              type: FormFieldType.TextDisplay,
              label: t('ID'),
              initialValue: '',
            },
            customerID: {
              type: FormFieldType.TextDisplay,
              label: t('Customer', { ns: 'common' }),
              config: {
                valueGetter: (field) =>
                  customers?.find((c) => c.id === field.value)?.name ||
                  t('Customer Not Found', { ns: 'common' }),
              },
            },
            firstName: {
              type: FormFieldType.TextDisplay,
              label: t('First Name', { ns: 'common' }),
            },
            lastName: {
              type: FormFieldType.TextDisplay,
              label: t('Last Name', { ns: 'common' }),
            },
            email: {
              type: FormFieldType.TextDisplay,
              label: t('Email', { ns: 'common' }),
            },
            settings: {
              type: FormFieldType.Nested,
              label: t('Settings', { ns: 'common' }),
              config: {
                language: {
                  type: FormFieldType.TextDisplay,
                  label: t('Language', { ns: 'common' }),
                  config: {
                    valueGetter: (field) =>
                      languagesOptions.find((l) => l.value === field.value)
                        ?.label || t('Language Not Found', { ns: 'common' }),
                  },
                },
                units: {
                  type: FormFieldType.TextDisplay,
                  label: t('Units', { ns: 'common' }),
                  config: {
                    valueGetter: (field) => t(field.value),
                  },
                },
                onboardingComplete: {
                  type: FormFieldType.CheckboxDisplay,
                  label: t('Onboarding Complete', { ns: 'common' }),
                },
              },
            },
          },
        },
      },
    },
  };
  return <GenericForm {...crudConfig} ref={formControlsRef} />;
};
